/**
 * RailChildRow — one sortable child of a group in the editable WHERE/HAVING
 * Rail (Slice 3B.6). A grip handle (drag) + the 3B.5 select checkbox + the
 * child node. `useSortable` id is `String(index)` (scoped to the parent
 * group's SortableContext). Drag listeners live on the grip only, so the
 * checkbox and leaf click-to-edit keep working.
 *
 * HAVING parity (Plan 3 Task 5): `kind` (default `'where'`) parameterizes the
 * testids and is forwarded to `RailSelectCheckbox`.
 */

import type { ReactNode } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical } from 'lucide-react';

import type { BooleanClauseTarget } from '../../../../sql-resolver/types/structuralEdit';
import type { RailSelection } from './railSelection';
import { isChildSelected } from './railSelection';
import { RailSelectCheckbox } from './RailSelectCheckbox';

export function RailChildRow({
  parentPath,
  index,
  selection,
  onToggleSelect,
  children,
  kind = 'where',
}: {
  readonly parentPath: readonly number[];
  readonly index: number;
  readonly selection?: RailSelection;
  readonly onToggleSelect?: (parentPath: readonly number[], index: number, shift: boolean) => void;
  readonly children: ReactNode;
  readonly kind?: BooleanClauseTarget;
}) {
  const childKey = [...parentPath, index].join('-') || 'root';
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: String(index),
  });
  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  } as const;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="group/row flex items-start gap-1"
      data-testid={`vqe-${kind}-rail-row-${childKey}`}
    >
      <span
        className="mt-1 shrink-0 cursor-grab opacity-0 transition-opacity active:cursor-grabbing group-hover/row:opacity-100"
        data-testid={`vqe-${kind}-rail-drag-${childKey}`}
        aria-label="Drag to reorder"
        {...attributes}
        {...listeners}
      >
        <GripVertical aria-hidden className="h-3.5 w-3.5 text-muted-foreground/60" />
      </span>
      {onToggleSelect ? (
        <RailSelectCheckbox
          parentPath={parentPath}
          index={index}
          checked={isChildSelected(selection ?? null, parentPath, index)}
          onToggle={onToggleSelect}
          kind={kind}
        />
      ) : null}
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}
